import type { Notification } from './Notifications.type';
import { defineStore } from 'pinia';

import { ref, watch } from 'vue';

export const useNotificationsStore = defineStore('Notifications', () => {
  const notificationsList = ref<Notification[]>([]);
  const activeNotification = ref<Notification | undefined>();

  let isChecking = false;
  let abortController: AbortController | null = null;

  const addNotification = (item: { subtitle: string }) => {
    notificationsList.value.push({ id: notificationsList.value.length, ...item });
  };

  const removeNotification = () => {
    if (abortController) {
      abortController.abort();
    }
  };

  function delay(ms: number, signal: AbortSignal): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const id = setTimeout(() => resolve(false), ms);
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(id);
          resolve(true);
        },
        { once: true },
      );
    });
  }

  async function checkNotifications() {
    if (isChecking) return;
    isChecking = true;

    while (notificationsList.value.length > 0) {
      activeNotification.value = notificationsList.value.shift();
      abortController = new AbortController();

      await delay(5000, abortController.signal);

      activeNotification.value = undefined;
      abortController = null;

      // pause between animations
      await new Promise(res => setTimeout(res, 800));
    }

    isChecking = false;
  }

  watch(
    notificationsList,
    (newValue) => {
      if (newValue.length > 0) {
        checkNotifications();
      }
    },
    { deep: true },
  );

  return { notificationsList, activeNotification, addNotification, removeNotification };
});
